import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Tag, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { useCreatePlan, useDeletePlan, usePlans, useServiceGroups, useUpdatePlan } from "@/hooks/queries";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import type { Plan } from "@/services/gpsdrive-plans.service";
import type { ServiceGroup } from "@/services/gpsdrive-assistance.service";

export const Route = createFileRoute("/admin/plans")({
  component: () => (
    <AdminAccessGate action="conta_integracoes">
      <PlansPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Planos — Administração — Wp Rastreamento" }] }),
});

const currencyFormatter = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

function PlansPage() {
  const { data: plans, isLoading } = usePlans();
  const { data: groups } = useServiceGroups();

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Planos</h1>
          <p className="text-sm text-muted-foreground">
            Nome, valor e descrição de cada plano — usados para escolher o plano real de cada
            veículo ao criar uma Recorrência (aba Faturamento). Um plano pode incluir um grupo de
            assistência (Administração → Configurar Assistência).
          </p>
        </div>
        <PlanFormDialog
          groups={groups ?? []}
          trigger={
            <Button>
              <Plus className="h-4 w-4" />
              Criar plano
            </Button>
          }
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : !plans || plans.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <Tag className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhum plano criado ainda</p>
            <p className="text-xs text-muted-foreground">
              Clique em "Criar plano" para cadastrar nome, valor e descrição do primeiro plano.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} groups={groups ?? []} />
          ))}
        </div>
      )}
    </div>
  );
}

function PlanCard({ plan, groups }: { plan: Plan; groups: ServiceGroup[] }) {
  const deletePlan = useDeletePlan();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const groupName = plan.serviceGroupId
    ? groups.find((g) => g.id === plan.serviceGroupId)?.name
    : null;

  const handleDelete = async () => {
    try {
      await deletePlan.mutateAsync(plan.id);
      toast.success("Plano excluído");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao excluir o plano");
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="flex-1">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">{plan.name}</CardTitle>
          <span className="whitespace-nowrap text-sm font-semibold text-primary">
            {currencyFormatter.format(plan.value)}
          </span>
        </div>
        {plan.description && <CardDescription className="line-clamp-3">{plan.description}</CardDescription>}
        {groupName && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <LifeBuoy className="h-3.5 w-3.5 shrink-0" />
            Com assistência: {groupName}
          </div>
        )}
      </CardHeader>
      <CardContent className="flex items-center justify-end gap-2 pt-0">
        <PlanFormDialog
          plan={plan}
          groups={groups}
          trigger={
            <Button variant="outline" size="sm">
              <Pencil className="h-3.5 w-3.5" />
              Editar
            </Button>
          }
        />
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
            <Trash2 className="h-3.5 w-3.5" />
            Excluir
          </Button>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir "{plan.name}"?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. Recorrências já criadas com este plano não são
                afetadas.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deletePlan.isPending}>
                {deletePlan.isPending ? "Excluindo…" : "Excluir"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

function PlanFormDialog({
  plan,
  groups,
  trigger,
}: {
  plan?: Plan;
  groups: ServiceGroup[];
  trigger: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [description, setDescription] = useState("");
  const [withAssistance, setWithAssistance] = useState(false);
  const [serviceGroupId, setServiceGroupId] = useState<string>("");
  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();
  const isEditing = Boolean(plan);
  const isPending = createPlan.isPending || updatePlan.isPending;

  useEffect(() => {
    if (!open) return;
    setName(plan?.name ?? "");
    setValue(plan ? String(plan.value) : "");
    setDescription(plan?.description ?? "");
    setWithAssistance(Boolean(plan?.serviceGroupId));
    setServiceGroupId(plan?.serviceGroupId ?? "");
  }, [open, plan]);

  const handleSave = async () => {
    const numericValue = Number(value.replace(",", "."));
    if (!name.trim()) {
      toast.error("Informe o nome do plano");
      return;
    }
    if (!numericValue || numericValue <= 0) {
      toast.error("Informe um valor maior que zero");
      return;
    }
    if (withAssistance && !serviceGroupId) {
      toast.error("Escolha a assistência incluída neste plano");
      return;
    }
    try {
      const input = {
        name: name.trim(),
        description: description.trim() || null,
        value: numericValue,
        serviceGroupId: withAssistance ? serviceGroupId : null,
      };
      if (plan) {
        await updatePlan.mutateAsync({ id: plan.id, input });
        toast.success("Plano atualizado");
      } else {
        await createPlan.mutateAsync(input);
        toast.success("Plano criado");
      }
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar o plano");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar plano" : "Criar plano"}</DialogTitle>
          <DialogDescription>
            Este plano fica disponível para escolher ao criar uma Recorrência na aba Faturamento.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="plan-name">Nome</Label>
            <Input id="plan-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Básico" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plan-value">Valor</Label>
            <Input
              id="plan-value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="49,90"
              inputMode="decimal"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plan-description">Descrição</Label>
            <Textarea
              id="plan-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="O que está incluso neste plano…"
            />
          </div>

          <div className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="plan-with-assistance">Plano com assistência</Label>
              <Switch
                id="plan-with-assistance"
                checked={withAssistance}
                onCheckedChange={(checked) => {
                  setWithAssistance(checked);
                  if (!checked) setServiceGroupId("");
                }}
              />
            </div>
            {withAssistance && (
              <div className="space-y-2">
                <Label htmlFor="plan-service-group">Assistência incluída</Label>
                {groups.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Nenhum grupo de assistência cadastrado ainda — crie um em Administração →
                    Configurar Assistência.
                  </p>
                ) : (
                  <Select value={serviceGroupId} onValueChange={setServiceGroupId}>
                    <SelectTrigger id="plan-service-group">
                      <SelectValue placeholder="Escolha o grupo" />
                    </SelectTrigger>
                    <SelectContent>
                      {groups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
